import { useState, useCallback, useEffect } from 'react'

export interface ValidationRule {
  required?: boolean
  minLength?: number
  maxLength?: number
  pattern?: RegExp
  patternMessage?: string
  validate?: (value: string) => string | null
  type?: 'email' | 'phone' | 'url' | 'number' | 'text'
}

export interface UseInputValidationReturn {
  value: string
  error: string | null
  touched: boolean
  isValid: boolean
  setValue: (value: string) => void
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void
  handleBlur: () => void
  reset: () => void
  validate: () => boolean
}

export const useInputValidation = (
  initialValue: string = '',
  rules: ValidationRule = {},
  dependencies: any[] = []
): UseInputValidationReturn => {
  const [value, setValue] = useState(initialValue)
  const [error, setError] = useState<string | null>(null)
  const [touched, setTouched] = useState(false)
  const [isValid, setIsValid] = useState(false)

  // Réinitialiser quand les dépendances changent
  useEffect(() => {
    setValue(initialValue)
    setError(null)
    setTouched(false)
    setIsValid(false)
  }, dependencies)

  const validateValue = useCallback((val: string): string | null => {
    // Validation required
    if (rules.required && (!val || val.trim() === '')) {
      return 'Ce champ est requis'
    }

    // Validation minLength (seulement si valeur non vide)
    if (rules.minLength && val && val.length < rules.minLength) {
      return `Minimum ${rules.minLength} caractères`
    }

    // Validation maxLength (seulement si valeur non vide)
    if (rules.maxLength && val && val.length > rules.maxLength) {
      return `Maximum ${rules.maxLength} caractères`
    }

    // Validation pattern (seulement si valeur non vide)
    if (rules.pattern && val && !rules.pattern.test(val)) {
      return rules.patternMessage || 'Format invalide'
    }

    // Validation type spécifique
    if (rules.type === 'email' && val) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(val)) {
        return 'Format email invalide'
      }
    }

    if (rules.type === 'phone' && val) {
      // Supprimer les espaces et caractères spéciaux pour la validation
      const cleaned = val.replace(/[^\d+]/g, '')
      if (cleaned.startsWith('+')) {
        // Format international: + suivi de 8-15 chiffres
        if (!/^\+\d{8,15}$/.test(cleaned)) {
          return 'Format international invalide (8-15 chiffres après +)'
        }
      } else {
        // Format national: 10 chiffres pour la France
        if (!/^\d{10}$/.test(cleaned)) {
          return 'Numéro français invalide (10 chiffres requis)'
        }
      }
    }

    if (rules.type === 'url' && val) {
      try {
        new URL(val)
      } catch {
        return 'URL invalide'
      }
    }

    if (rules.type === 'number' && val) {
      if (isNaN(Number(val))) {
        return 'Valeur numérique requise'
      }
    }

    // Validation custom
    if (rules.validate) {
      const customError = rules.validate(val)
      if (customError) {
        return customError
      }
    }

    return null
  }, [rules])

  const handleChange = useCallback((
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const newValue = e.target.value
    setValue(newValue)
    
    // Validation en temps réel seulement si déjà touché
    if (touched) {
      const validationError = validateValue(newValue)
      setError(validationError)
      setIsValid(!validationError)
    }
  }, [touched, validateValue])

  const handleBlur = useCallback(() => {
    setTouched(true)
    const validationError = validateValue(value)
    setError(validationError)
    setIsValid(!validationError)
  }, [value, validateValue])

  const setValueWithValidation = useCallback((newValue: string) => {
    setValue(newValue)
    if (touched) {
      const validationError = validateValue(newValue)
      setError(validationError)
      setIsValid(!validationError)
    }
  }, [touched, validateValue])

  const reset = useCallback(() => {
    setValue(initialValue)
    setError(null)
    setTouched(false)
    setIsValid(false)
  }, [initialValue])

  const validate = useCallback((): boolean => {
    const validationError = validateValue(value)
    setError(validationError)
    setIsValid(!validationError)
    setTouched(true)
    return !validationError
  }, [value, validateValue])

  // Validation initiale si value initiale fournie
  useEffect(() => {
    if (initialValue) {
      const validationError = validateValue(initialValue)
      setError(validationError)
      setIsValid(!validationError)
    }
  }, [])

  return {
    value,
    error,
    touched,
    isValid,
    setValue: setValueWithValidation,
    handleChange,
    handleBlur,
    reset,
    validate
  }
}

// Helpers pour les règles communes
export const emailRules: ValidationRule = {
  required: true,
  type: 'email',
  maxLength: 255
}

export const phoneRules: ValidationRule = {
  required: true,
  type: 'phone',
  minLength: 10,
  maxLength: 20
}

export const nameRules: ValidationRule = {
  required: true,
  minLength: 2,
  maxLength: 50,
  pattern: /^[a-zA-ZÀ-ÿ\s'-]+$/,
  patternMessage: 'Lettres, espaces, apostrophes et tirets uniquement'
}

export const passwordRules: ValidationRule = {
  required: true,
  minLength: 8,
  maxLength: 100,
  validate: (value: string) => {
    if (!/(?=.*[a-z])/.test(value)) {
      return 'Au moins une minuscule'
    }
    if (!/(?=.*[A-Z])/.test(value)) {
      return 'Au moins une majuscule'
    }
    if (!/(?=.*\d)/.test(value)) {
      return 'Au moins un chiffre'
    }
    if (!/(?=.*[@$!%*?&])/.test(value)) {
      return 'Au moins un caractère spécial (@$!%*?&)'
    }
    return null
  }
}

export const priceRules: ValidationRule = {
  required: true,
  type: 'number',
  validate: (value: string) => {
    const numValue = parseFloat(value)
    if (isNaN(numValue)) {
      return 'Valeur numérique requise'
    }
    if (numValue < 0) {
      return 'Le prix ne peut pas être négatif'
    }
    if (numValue > 1000000) {
      return 'Prix maximum: 1 000 000'
    }
    
    // Vérifier les décimales
    const decimalParts = value.split('.')
    if (decimalParts[1] && decimalParts[1].length > 2) {
      return 'Maximum 2 décimales'
    }
    
    return null
  }
}

export const quantityRules: ValidationRule = {
  required: true,
  validate: (value: string) => {
    const intValue = parseInt(value, 10)
    if (isNaN(intValue)) {
      return 'Nombre entier requis'
    }
    if (intValue < 0) {
      return 'La quantité ne peut pas être négative'
    }
    if (intValue > 100000) {
      return 'Quantité maximum: 100 000'
    }
    return null
  }
}

// Nouvelles règles pour les types de commerce
export const businessTypeRules: ValidationRule = {
  required: true,
  minLength: 2,
  maxLength: 50,
  pattern: /^[a-zA-ZÀ-ÿ\s&'-]+$/,
  patternMessage: 'Lettres, espaces, apostrophes, tirets et & uniquement'
}

// Règles pour les IDs fiscaux (SIRET, TVA, etc.)
export const taxIdRules: ValidationRule = {
  required: false,
  minLength: 9,
  maxLength: 20,
  pattern: /^[A-Z0-9\s-]+$/,
  patternMessage: 'Lettres majuscules, chiffres, espaces et tirets uniquement',
  validate: (value: string) => {
    if (!value || value.trim() === '') {
      return null // Optionnel
    }
    
    const cleaned = value.replace(/[\s-]/g, '')
    
    // Validation SIRET français (14 chiffres)
    if (/^\d{14}$/.test(cleaned)) {
      // Algorithme de validation Luhn pour SIRET
      let sum = 0
      let isEven = false
      
      for (let i = cleaned.length - 1; i >= 0; i--) {
        let digit = parseInt(cleaned.charAt(i), 10)
        
        if (isEven) {
          digit *= 2
          if (digit > 9) {
            digit -= 9
          }
        }
        
        sum += digit
        isEven = !isEven
      }
      
      if (sum % 10 !== 0) {
        return 'Numéro SIRET invalide (algorithme Luhn)'
      }
    }
    
    // Validation TVA intracommunautaire (format européen)
    if (/^[A-Z]{2}[A-Z0-9\s]{2,13}$/.test(cleaned)) {
      // Format de base valide, validation spécifique par pays serait complexe
      // On accepte le format général
    }
    
    return null
  }
}

// Règles pour les pourcentages (0-100)
export const percentageRules: ValidationRule = {
  required: false,
  validate: (value: string) => {
    if (!value || value.trim() === '') {
      return null // Optionnel
    }
    
    const numValue = parseFloat(value)
    if (isNaN(numValue)) {
      return 'Valeur numérique requise'
    }
    
    if (numValue < 0) {
      return 'Le pourcentage ne peut pas être négatif'
    }
    
    if (numValue > 100) {
      return 'Pourcentage maximum: 100%'
    }
    
    // Vérifier les décimales
    const decimalParts = value.split('.')
    if (decimalParts[1] && decimalParts[1].length > 2) {
      return 'Maximum 2 décimales'
    }
    
    return null
  }
}

// Règles pour les dates (format YYYY-MM-DD)
export const dateRules: ValidationRule = {
  required: false,
  pattern: /^\d{4}-\d{2}-\d{2}$/,
  patternMessage: 'Format de date invalide (YYYY-MM-DD requis)',
  validate: (value: string) => {
    if (!value || value.trim() === '') {
      return null // Optionnel
    }
    
    const date = new Date(value)
    if (isNaN(date.getTime())) {
      return 'Date invalide'
    }
    
    // Vérifier que la date est dans un format valide
    const parts = value.split('-')
    if (parts.length !== 3) {
      return 'Format de date invalide (YYYY-MM-DD requis)'
    }
    
    const year = parseInt(parts[0], 10)
    const month = parseInt(parts[1], 10)
    const day = parseInt(parts[2], 10)
    
    if (year < 1900 || year > 2100) {
      return 'Année doit être entre 1900 et 2100'
    }
    
    if (month < 1 || month > 12) {
      return 'Mois doit être entre 1 et 12'
    }
    
    // Vérification basique des jours (approximative)
    if (day < 1 || day > 31) {
      return 'Jour doit être entre 1 et 31'
    }
    
    return null
  }
}

// Règles pour les codes (alphanumériques)
export const codeRules: ValidationRule = {
  required: false,
  minLength: 1,
  maxLength: 20,
  pattern: /^[A-Z0-9_-]+$/,
  patternMessage: 'Lettres majuscules, chiffres, tirets et underscores uniquement'
}

// Règles pour les descriptions (texte long)
export const descriptionRules: ValidationRule = {
  required: false,
  minLength: 0,
  maxLength: 1000,
  validate: (value: string) => {
    if (value.length > 1000) {
      return 'Description trop longue (maximum 1000 caractères)'
    }
    return null
  }
}

// Règles pour les URLs
export const urlRules: ValidationRule = {
  required: false,
  type: 'url',
  maxLength: 500
}

// Règles pour les emails (optionnels)
export const optionalEmailRules: ValidationRule = {
  required: false,
  type: 'email',
  maxLength: 255
}

// Règles pour les noms de commerce/entreprise
export const businessNameRules: ValidationRule = {
  required: true,
  minLength: 2,
  maxLength: 100,
  pattern: /^[a-zA-ZÀ-ÿ0-9\s&'.,-]+$/,
  patternMessage: 'Lettres, chiffres, espaces, apostrophes, points, virgules, tirets et & uniquement'
}

// Formatter helpers
export const formatPhoneNumber = (value: string): string => {
  if (!value) return ''
  
  // Garder seulement chiffres et +
  let cleaned = value.replace(/[^\d+]/g, '')
  
  // Si commence par +, garder le +
  const hasPlus = cleaned.startsWith('+')
  if (hasPlus) {
    cleaned = '+' + cleaned.slice(1).replace(/\D/g, '')
  } else {
    cleaned = cleaned.replace(/\D/g, '')
  }
  
  // Formatage selon la longueur
  if (hasPlus) {
    // Format international
    if (cleaned.length <= 4) return cleaned
    if (cleaned.length <= 7) return `${cleaned.slice(0, 3)} ${cleaned.slice(3)}`
    return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 5)} ${cleaned.slice(5, 8)} ${cleaned.slice(8, 10)} ${cleaned.slice(10)}`
  } else {
    // Format national (France)
    if (cleaned.length <= 2) return cleaned
    if (cleaned.length <= 4) return `${cleaned.slice(0, 2)} ${cleaned.slice(2)}`
    if (cleaned.length <= 6) return `${cleaned.slice(0, 2)} ${cleaned.slice(2, 4)} ${cleaned.slice(4)}`
    return `${cleaned.slice(0, 2)} ${cleaned.slice(2, 4)} ${cleaned.slice(4, 6)} ${cleaned.slice(6, 8)} ${cleaned.slice(8)}`
  }
}

export const formatPrice = (value: string): string => {
  if (!value) return ''
  
  // Supprimer tout sauf chiffres et point
  let cleaned = value.replace(/[^\d.]/g, '')
  
  // Garder seulement le premier point
  const parts = cleaned.split('.')
  if (parts.length > 2) {
    cleaned = parts[0] + '.' + parts.slice(1).join('')
  }
  
  // Limiter à 2 décimales
  if (parts[1] && parts[1].length > 2) {
    cleaned = parts[0] + '.' + parts[1].slice(0, 2)
  }
  
  return cleaned
}

export const formatName = (value: string): string => {
  if (!value) return ''
  
  // Capitaliser la première lettre de chaque mot
  return value
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim()
}

// Formatter pour les IDs fiscaux (SIRET, TVA)
export const formatTaxId = (value: string): string => {
  if (!value) return ''
  
  // Supprimer tous les caractères non alphanumériques sauf espaces et tirets
  let cleaned = value.replace(/[^A-Z0-9\s-]/gi, '').toUpperCase()
  
  // Format SIRET: 14 chiffres avec espace tous les 3 chiffres
  if (/^\d{14}$/.test(cleaned.replace(/[\s-]/g, ''))) {
    const digits = cleaned.replace(/[\s-]/g, '')
    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 9)} ${digits.slice(9, 12)} ${digits.slice(12, 14)}`
  }
  
  // Format TVA intracommunautaire: FR XX XXX XXX XXX
  if (/^FR[A-Z0-9]{2,13}$/i.test(cleaned.replace(/[\s-]/g, ''))) {
    const cleanedNoSpaces = cleaned.replace(/[\s-]/g, '')
    if (cleanedNoSpaces.length <= 4) return cleanedNoSpaces
    return `${cleanedNoSpaces.slice(0, 2)} ${cleanedNoSpaces.slice(2, 4)} ${cleanedNoSpaces.slice(4, 7)} ${cleanedNoSpaces.slice(7, 10)} ${cleanedNoSpaces.slice(10)}`
  }
  
  return cleaned
}

// Formatter pour les pourcentages
export const formatPercentage = (value: string): string => {
  if (!value) return ''
  
  // Supprimer tout sauf chiffres et point
  let cleaned = value.replace(/[^\d.]/g, '')
  
  // Garder seulement le premier point
  const parts = cleaned.split('.')
  if (parts.length > 2) {
    cleaned = parts[0] + '.' + parts.slice(1).join('')
  }
  
  // Limiter à 2 décimales
  if (parts[1] && parts[1].length > 2) {
    cleaned = parts[0] + '.' + parts[1].slice(0, 2)
  }
  
  // Ajouter le signe % si non présent
  if (!value.includes('%')) {
    return cleaned
  }
  
  return cleaned
}

// Formatter pour les dates (YYYY-MM-DD)
export const formatDate = (value: string): string => {
  if (!value) return ''
  
  // Supprimer tous les caractères non numériques sauf tirets
  let cleaned = value.replace(/[^\d-]/g, '')
  
  // Format automatique: YYYY-MM-DD
  if (cleaned.length <= 4) {
    return cleaned
  } else if (cleaned.length <= 6) {
    return `${cleaned.slice(0, 4)}-${cleaned.slice(4)}`
  } else if (cleaned.length <= 8) {
    return `${cleaned.slice(0, 4)}-${cleaned.slice(4, 6)}-${cleaned.slice(6)}`
  } else {
    return `${cleaned.slice(0, 4)}-${cleaned.slice(4, 6)}-${cleaned.slice(6, 8)}`
  }
}

// Formatter pour les codes (majuscules, sans espaces)
export const formatCode = (value: string): string => {
  if (!value) return ''
  
  // Convertir en majuscules et supprimer les espaces
  return value.toUpperCase().replace(/\s+/g, '')
}

// Formatter pour les noms de commerce (capitalisation intelligente)
export const formatBusinessName = (value: string): string => {
  if (!value) return ''
  
  // Mots à ne pas capitaliser (articles, prépositions)
  const lowercaseWords = ['le', 'la', 'les', 'de', 'du', 'des', 'et', 'ou', 'à', 'au', 'aux', 'en', 'dans', 'sur', 'sous', 'pour', 'par', 'avec', 'sans', 'chez']
  
  return value
    .toLowerCase()
    .split(' ')
    .map((word, index) => {
      // Toujours capitaliser le premier mot
      if (index === 0) {
        return word.charAt(0).toUpperCase() + word.slice(1)
      }
      
      // Ne pas capitaliser les mots en minuscule listés
      if (lowercaseWords.includes(word)) {
        return word
      }
      
      // Capitaliser les autres mots
      return word.charAt(0).toUpperCase() + word.slice(1)
    })
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim()
}

// Formatter pour les URLs (ajout de https:// si manquant)
export const formatUrl = (value: string): string => {
  if (!value) return ''
  
  // Si déjà commence par http:// ou https://, laisser tel quel
  if (/^https?:\/\//i.test(value)) {
    return value
  }
  
  // Sinon, ajouter https://
  return `https://${value}`
}

// Helper pour obtenir la classe CSS basée sur l'état de validation
export const getInputClassName = (hasError: boolean, isValid: boolean, additionalClasses: string = ''): string => {
  let className = 'w-full rounded-lg border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-offset-1 '
  
  if (hasError) {
    className += 'border-red-300 focus:border-red-500 focus:ring-red-500 bg-red-50 '
  } else if (isValid) {
    className += 'border-green-300 focus:border-green-500 focus:ring-green-500 bg-green-50 '
  } else {
    className += 'border-gray-300 focus:border-primary-500 focus:ring-primary-500 '
  }
  
  return className + additionalClasses
}

// Helper pour générer le HTML des messages d'erreur (à utiliser dans les composants)
export const getErrorMessageHtml = (message: string | null): string => {
  if (!message) return ''
  
  return `<p class="mt-1 text-sm text-red-600">${message}</p>`
}

// Helper pour générer les classes CSS des indicateurs de validation
export const getValidationIndicatorClasses = (isValid: boolean, isTouched: boolean): string => {
  if (!isTouched) return ''
  
  if (isValid) {
    return 'absolute right-3 top-1/2 transform -translate-y-1/2'
  }
  
  return 'absolute right-3 top-1/2 transform -translate-y-1/2'
}

// Helper pour obtenir l'icône de validation (nom de classe)
export const getValidationIcon = (isValid: boolean, isTouched: boolean): string => {
  if (!isTouched) return ''
  
  if (isValid) {
    return 'text-green-600'
  }
  
  return 'text-red-600'
}
